import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';

import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { GlobalRole, SiteRole } from 'src/features/auth';
import { SITE_LOADING_KEY } from 'src/features/site';
import { RootState } from 'src/store';

interface IWithRoleAccessProps {
	allowedGlobalRoles?: GlobalRole[];
	allowedSiteRoles?: SiteRole[];
	children: React.ReactNode;
	rerouteTo?: string;
	fallBackChildren?: React.ReactNode;
}

const WithRoleAccess: React.FC<IWithRoleAccessProps> = ({
	allowedGlobalRoles,
	allowedSiteRoles,
	children,
	rerouteTo,
	fallBackChildren,
}) => {
	const userType = useSelector((state: RootState) => state.auth.user_type);
	const { siteSlug } = useParams();
	const siteRole = useSelector(
		(state: RootState) => siteSlug && state.site.siteRolesMap[siteSlug],
	);
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[SITE_LOADING_KEY],
	);

	// Safety check to refetch site role mapping for user if not found.
	if (allowedSiteRoles && !siteRole && !isLoading) {
		snackbar(
			'Could not determine your permissions. Please Try again',
			'warning',
		);
		return <Navigate to='/dashboard' replace />;
	}

	const hasGlobalAccess = allowedGlobalRoles?.length
		? allowedGlobalRoles.includes(userType as GlobalRole)
		: true;
	const hasSiteAccess = allowedSiteRoles?.length
		? allowedSiteRoles.some((role) => siteRole?.includes(role))
		: true;

	if (!hasGlobalAccess || !hasSiteAccess) {
		if (rerouteTo) return <Navigate to={rerouteTo} replace />;
		if (fallBackChildren) return <>{fallBackChildren}</>;
		return null;
	}

	return <>{children}</>;
};

export default WithRoleAccess;
